import { useCallback, useEffect, useMemo, useState } from "react";

import { getAssignableMenuGroups } from "@/api/system/roles";
import type { GetAssignableMenuGroupsParams, RoleMenuGroupVO } from "@/api/system/roles/type";

import {
  collectMenuTreeNodeIds,
  flattenPermissionSelection,
  groupSelectedIdsByApp,
  type PermissionSelectionByApp,
  syncParentPermissionIds,
} from "../utils";
export type { PermissionSelectionByApp } from "../utils";

/** usePermission 入参 */
export interface UsePermissionParams {
  /** getAssignableMenuGroups 查询参数 */
  params?: GetAssignableMenuGroupsParams;
  /** 已选权限 ID（受控）：支持扁平 id 或按端分组（Form 回写） */
  value?: string[] | PermissionSelectionByApp[];
  /** 默认已选权限 ID（非受控） */
  defaultValue?: string[];
  /** 选中变化回调，按端返回已选 menuIds */
  onChange?: (selectionByApp: PermissionSelectionByApp[]) => void;
  /** 只读展示，不可勾选 */
  readOnly?: boolean;
}

/**
 * 按端分组权限选择：拉取菜单分组、管理选中与展开状态
 */
export const usePermission = ({
  params,
  value,
  defaultValue = [],
  onChange,
  readOnly = false,
}: UsePermissionParams) => {
  // 接口返回的按端分组菜单树
  const [permissionList, setPermissionList] = useState<RoleMenuGroupVO[]>([]);
  // 分组数据加载中
  const [loading, setLoading] = useState(false);
  // 已展开的端 panel key（systemTypeId）
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  // 非受控模式下的已选权限 id
  const [innerSelectedIds, setInnerSelectedIds] = useState<string[]>(defaultValue);

  /** 当前生效的已选 id：受控时扁平化 value，非受控用内部状态 */
  const selectedIds = useMemo(
    () => (value !== undefined ? flattenPermissionSelection(value) : innerSelectedIds),
    [value, innerSelectedIds],
  );
  // 接口查询参数字段，拆开后作为 effect 依赖避免对象引用变化导致重复请求
  const { orgId, orgType, roleId } = params ?? {};

  /** 写入已选 id 并按端分组触发 onChange */
  const updateSelectedIds = useCallback(
    (nextIds: string[]) => {
      if (value === undefined) {
        setInnerSelectedIds(nextIds);
      }
      onChange?.(groupSelectedIdsByApp(nextIds, permissionList));
    },
    [onChange, permissionList, value],
  );

  /** 受控 value 变化时同步非受控缓存（统一为扁平 id） */
  useEffect(() => {
    if (value !== undefined) {
      setInnerSelectedIds(flattenPermissionSelection(value));
    }
  }, [value]);

  /** params 变化时拉取可选菜单分组 */
  useEffect(() => {
    let cancelled = false;
    const loadPermissionGroups = async () => {
      setLoading(true);
      try {
        // 获取可选菜单分组
        const res = await getAssignableMenuGroups({ orgType });
        if (!cancelled) {
          // menuTree 为空的端无可分配权限，不展示该分组
          const groupsWithMenu = (res.data ?? []).filter(
            (item) => (item.menuTree?.length ?? 0) > 0,
          );
          setPermissionList(groupsWithMenu);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    void loadPermissionGroups();
    return () => {
      cancelled = true;
    };
  }, [orgId, orgType, roleId]);

  /** 勾选/取消权限节点（含父子批量勾选，子级全选时自动补齐父级 id） */
  const handleTogglePermission = useCallback(
    (code: string, checked: boolean, childCodes: string[]) => {
      if (readOnly) return;
      const idsToUpdate = [...new Set(childCodes.length > 0 ? [code, ...childCodes] : [code])];
      const nextSet = new Set(selectedIds);
      idsToUpdate.forEach((id) => {
        if (checked) {
          nextSet.add(id);
        } else {
          nextSet.delete(id);
        }
      });
      const allTrees = permissionList.flatMap((group) => group.menuTree ?? []);
      updateSelectedIds(syncParentPermissionIds([...nextSet], allTrees));
    },
    [permissionList, readOnly, selectedIds, updateSelectedIds],
  );

  /** 切换权限树节点展开状态；isApp 为 true 时同步展开/收起该端下全部子菜单 */
  const handleToggleExpand = useCallback(
    (nodeId: string, isApp?: boolean) => {
      setExpandedIds((prev) => {
        const next = new Set(prev);
        if (isApp) {
          const appGroup = permissionList.find((item) => item.systemTypeId === nodeId);
          const childIds = appGroup ? collectMenuTreeNodeIds(appGroup.menuTree ?? []) : [];
          const relatedIds = [nodeId, ...childIds];
          const isCollapsing = next.has(nodeId);
          relatedIds.forEach((id) => {
            if (isCollapsing) {
              next.delete(id);
            } else {
              next.add(id);
            }
          });
          return next;
        }
        if (next.has(nodeId)) {
          next.delete(nodeId);
        } else {
          next.add(nodeId);
        }
        return next;
      });
    },
    [permissionList],
  );

  return {
    loading,
    groups: permissionList,
    selectedIds,
    expandedIds,
    handleToggleExpand,
    handleTogglePermission,
  };
};
